<template>
  <Teleport to="body">
    <div
      v-if="mark && targetRect"
      class="coach-mark"
      role="region"
      :aria-label="shownMark.title"
    >
      <div
        class="coach-mark__spotlight"
        :style="spotlightStyle"
        aria-hidden="true"
      />

      <v-card
        class="coach-mark__card pa-4"
        :style="cardStyle"
        elevation="8"
        rounded="lg"
        max-width="320"
        :dir="chromeHebrew ? 'rtl' : 'ltr'"
        :lang="chromeHebrew ? 'he' : 'en'"
      >
        <div
          v-if="walkthroughProgress"
          class="text-caption text-primary font-weight-medium mb-1"
        >
          {{ chrome.quickTip }} {{ walkthroughProgress.current }} {{ chrome.of }}
          {{ walkthroughProgress.total }}
        </div>
        <div class="text-subtitle-1 font-weight-bold mb-1">{{ shownMark.title }}</div>
        <p class="text-body-2 text-medium-emphasis mb-3">{{ shownMark.body }}</p>
        <div class="d-flex align-center ga-2">
          <v-btn
            v-if="markVideo"
            variant="tonal"
            color="orange"
            size="small"
            prepend-icon="mdi-play-circle-outline"
            data-cy="coach-mark-watch"
            @click="watchVideo"
          >
            {{ chrome.watchClip(tutorialSeconds(markVideo, chromeHebrew)) }}
          </v-btn>
          <v-spacer />
          <v-btn
            variant="text"
            size="small"
            data-cy="coach-mark-skip"
            @click="skipMark"
          >
            {{ chrome.skip }}
          </v-btn>
          <v-btn color="primary" variant="flat" size="small" data-cy="coach-mark-got-it" @click="gotIt">
            {{ chrome.gotIt }}
          </v-btn>
        </div>
      </v-card>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, onUnmounted, type CSSProperties } from "vue";
import { useOnboardingStore } from "../store/pinia/onboardingStore";
import { useTutorialStore } from "../store/pinia/tutorialStore";
import { usePracticeLocale } from "../composables/usePracticeLocale";
import { coachMarkText, helpChrome } from "../i18n/practiceChrome";
import { tutorialById, tutorialSeconds } from "../config/tutorials";
import {
  HELP_WORKS_MARK_ID,
  trackTutorialEvent,
} from "../helpers/tutorialAnalytics";
import { coachCardBox } from "../helpers/coachMarkLayout";

const onboarding = useOnboardingStore();
const tutorialStore = useTutorialStore();
const targetRect = ref<DOMRect | null>(null);
const bottomInset = ref(8);
let rafId = 0;
let trackTimer: ReturnType<typeof setInterval> | null = null;

const { chromeHebrew } = usePracticeLocale();
const chrome = computed(() => helpChrome(chromeHebrew.value));
const mark = computed(() => onboarding.activeCoachMark);
const shownMark = computed(() => {
  const current = mark.value;
  if (!current) return { title: "", body: "" };
  return coachMarkText(current.id, current.title, current.body, chromeHebrew.value);
});

const walkthroughProgress = computed(() =>
  onboarding.quickTipsProgress(mark.value?.id),
);

const markVideo = computed(() => {
  const id = mark.value?.videoId;
  return id ? tutorialById(id) : undefined;
});

function isHelpWorks(): boolean {
  return mark.value?.id === HELP_WORKS_MARK_ID;
}

/** The dialog sits above the coach mark; the mark stays put until Got it. */
function watchVideo() {
  if (isHelpWorks()) {
    trackTutorialEvent("tutorial_modal_watch", {
      markId: HELP_WORKS_MARK_ID,
      videoId: markVideo.value?.id,
    });
  }
  if (markVideo.value) tutorialStore.openTutorials([markVideo.value]);
}

function skipMark() {
  if (isHelpWorks()) {
    trackTutorialEvent("tutorial_modal_skip", { markId: HELP_WORKS_MARK_ID });
  }
  onboarding.dismissActiveCoachMark(true);
}

function gotIt() {
  if (isHelpWorks()) {
    trackTutorialEvent("tutorial_modal_got_it", { markId: HELP_WORKS_MARK_ID });
  }
  onboarding.dismissActiveCoachMark(true);
}

function readBottomInset(): number {
  const host = document.querySelector(".instruction-bar-host");
  if (!host) return 8;
  const rect = host.getBoundingClientRect();
  if (rect.height < 1) return 8;
  const overlap = window.innerHeight - rect.top;
  if (overlap < 8) return 8;
  return Math.ceil(overlap) + 8;
}

function sameRect(a: DOMRect | null, b: DOMRect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

function measureTarget() {
  const nextInset = readBottomInset();
  if (nextInset !== bottomInset.value) bottomInset.value = nextInset;
  if (!mark.value) {
    targetRect.value = null;
    return;
  }
  const el = document.querySelector(mark.value.targetSelector);
  const next = el?.getBoundingClientRect() ?? null;
  if (sameRect(targetRect.value, next)) return;
  targetRect.value = next;
}

function scheduleMeasure() {
  cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(measureTarget);
}

watch(
  mark,
  (next, prev) => {
    if (next?.id === HELP_WORKS_MARK_ID && prev?.id !== HELP_WORKS_MARK_ID) {
      trackTutorialEvent("tutorial_modal_open", {
        markId: HELP_WORKS_MARK_ID,
        videoId: next.videoId,
      });
    }
    if (trackTimer) {
      clearInterval(trackTimer);
      trackTimer = null;
    }
    if (next) {
      trackTimer = setInterval(measureTarget, 200);
    }
    scheduleMeasure();
  },
  { immediate: true },
);

if (typeof window !== "undefined") {
  window.addEventListener("resize", scheduleMeasure);
  window.addEventListener("scroll", scheduleMeasure, true);
}

onUnmounted(() => {
  cancelAnimationFrame(rafId);
  if (trackTimer) clearInterval(trackTimer);
  window.removeEventListener("resize", scheduleMeasure);
  window.removeEventListener("scroll", scheduleMeasure, true);
});

const spotlightStyle = computed(() => {
  if (!targetRect.value) return {};
  const pad = 6;
  const r = targetRect.value;
  return {
    top: `${r.top - pad}px`,
    left: `${r.left - pad}px`,
    width: `${r.width + pad * 2}px`,
    height: `${r.height + pad * 2}px`,
    boxShadow: "0 0 0 3px #f59e0b",
  };
});

const cardStyle = computed((): CSSProperties => {
  if (!targetRect.value) return { display: "none" };
  const r = targetRect.value;
  const inset = bottomInset.value;
  const narrow = window.innerWidth < 600;
  const box = coachCardBox(
    { width: window.innerWidth, height: window.innerHeight },
    { top: r.top, bottom: r.bottom, left: r.left },
    inset,
  );
  if (narrow && box.pin === "bottom") {
    return {
      position: "fixed",
      bottom: `${inset}px`,
      left: `${box.left}px`,
      width: `${box.width}px`,
      zIndex: 10002,
    };
  }
  if (narrow) {
    return {
      position: "fixed",
      top: `${box.top}px`,
      left: `${box.left}px`,
      width: `${box.width}px`,
      maxHeight: `${box.height}px`,
      overflow: "auto",
      zIndex: 10002,
    };
  }
  return {
    position: "fixed",
    top: `${box.top}px`,
    left: `${box.left}px`,
    width: `${box.width}px`,
    zIndex: 10002,
  };
});

</script>

<style scoped>
.coach-mark {
  position: fixed;
  inset: 0;
  z-index: 10000;
  pointer-events: none;
}

.coach-mark__spotlight {
  position: fixed;
  border-radius: 8px;
  pointer-events: none;
  z-index: 10001;
  background: transparent;
}

.coach-mark__card {
  pointer-events: auto;
  z-index: 10002;
}
</style>
